// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React from "react";

// -----------------------------------------------------------------------------
// Components and helpers
// -----------------------------------------------------------------------------
import { useParams } from "react-router";
import { CopyIcon } from "components/Icons/CopyIcon";
import { Flex, Typography, notification } from "antd";
import { useGetDomainGymQuery } from "store/api/api";
import { getClientHost } from "helpers/constants";
import alpha from "helpers/alpha";

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
function InviteBanner() {
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const clientHost = getClientHost();
	const inviteLink = `${clientHost}/${gym}`.replace("https://", "").replace("http://", "");
	const primaryColor = domainGymResponse?.data?.brand_color1 || "#ff865c";

	const handleCopyText = (text) => {
		navigator.clipboard.writeText(text);
		notification.success({
			message: "Text was copied to clipboard!",
		});
	};

	return (
		<Flex
			className="sb-invite-banner"
			style={{ "--background": alpha(primaryColor, 0.06), "--circle-background": alpha(primaryColor, 0.1) }}
			gap={16}
			data-id="sectionInviteBanner"
		>
			<Flex gap={16} flex={1} align="center">
				<Flex>
					<div className="sb-invite-banner__share-circle">
						<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M36 28.5C34.7102 28.5025 33.443 28.8386 32.3215 29.4758C31.2001 30.1129 30.2625 31.0293 29.6 32.136L18.944 26.8C19.6854 25.0028 19.6854 22.9852 18.944 21.188L29.6 15.864C30.542 17.4 32.0084 18.5429 33.7279 19.0813C35.4474 19.6198 37.3037 19.5173 38.9535 18.7929C40.6033 18.0685 41.935 16.7712 42.7022 15.1409C43.4694 13.5106 43.6204 11.6576 43.127 9.92457C42.6337 8.19159 41.5295 6.69589 40.0186 5.71409C38.5077 4.7323 36.6925 4.33084 34.9085 4.58397C33.1245 4.8371 31.4926 5.72768 30.3145 7.09101C29.1364 8.45435 28.4918 10.1982 28.5 12C28.5106 12.3393 28.5447 12.6774 28.602 13.012L17.274 18.676C16.2228 17.633 14.8863 16.9245 13.4331 16.6399C11.9798 16.3552 10.4748 16.5072 9.10782 17.0765C7.74079 17.6459 6.57291 18.6072 5.75139 19.8393C4.92987 21.0714 4.49149 22.5191 4.49149 24C4.49149 25.4809 4.92987 26.9286 5.75139 28.1607C6.57291 29.3928 7.74079 30.3541 9.10782 30.9235C10.4748 31.4928 11.9798 31.6448 13.4331 31.3601C14.8863 31.0755 16.2228 30.367 17.274 29.324L28.6 34.988C28.5433 35.3226 28.5099 35.6608 28.5 36C28.5 37.4834 28.9399 38.9334 29.764 40.1668C30.5881 41.4001 31.7594 42.3614 33.1299 42.9291C34.5003 43.4968 36.0083 43.6453 37.4632 43.3559C38.918 43.0665 40.2544 42.3522 41.3033 41.3033C42.3522 40.2544 43.0665 38.918 43.3559 37.4632C43.6453 36.0083 43.4968 34.5003 42.9291 33.1299C42.3614 31.7594 41.4002 30.5881 40.1668 29.764C38.9334 28.9399 37.4834 28.5 36 28.5ZM36 7.5C36.89 7.5 37.7601 7.76392 38.5001 8.25839C39.2401 8.75285 39.8169 9.45566 40.1575 10.2779C40.4981 11.1002 40.5872 12.005 40.4135 12.8779C40.2399 13.7508 39.8113 14.5526 39.182 15.182C38.5527 15.8113 37.7508 16.2399 36.8779 16.4135C36.005 16.5872 35.1002 16.4981 34.2779 16.1575C33.4557 15.8169 32.7529 15.2401 32.2584 14.5001C31.7639 13.76 31.5 12.89 31.5 12C31.5016 10.807 31.9762 9.66334 32.8198 8.81977C33.6634 7.9762 34.807 7.50159 36 7.5ZM12 28.5C11.11 28.5 10.24 28.2361 9.49994 27.7416C8.75992 27.2471 8.18314 26.5443 7.84255 25.7221C7.50195 24.8998 7.41284 23.995 7.58647 23.1221C7.76011 22.2492 8.18869 21.4474 8.81803 20.818C9.44736 20.1887 10.2492 19.7601 11.1221 19.5865C11.995 19.4128 12.8998 19.5019 13.7221 19.8425C14.5443 20.1831 15.2472 20.7599 15.7416 21.4999C16.2361 22.24 16.5 23.11 16.5 24C16.4984 25.193 16.0238 26.3367 15.1802 27.1802C14.3367 28.0238 13.193 28.4984 12 28.5ZM36 40.5C35.11 40.5 34.24 40.2361 33.4999 39.7416C32.7599 39.2471 32.1831 38.5443 31.8425 37.7221C31.502 36.8998 31.4128 35.995 31.5865 35.1221C31.7601 34.2492 32.1887 33.4474 32.818 32.818C33.4474 32.1887 34.2492 31.7601 35.1221 31.5865C35.995 31.4128 36.8998 31.5019 37.7221 31.8425C38.5443 32.1831 39.2472 32.7599 39.7416 33.4999C40.2361 34.24 40.5 35.11 40.5 36C40.4984 37.193 40.0238 38.3367 39.1802 39.1802C38.3367 40.0238 37.193 40.4984 36 40.5Z"
								fill={primaryColor}
							/>
						</svg>
					</div>
				</Flex>
				<div className="sb-invite-banner__text-container">
					<Typography.Title className="sb-invite-banner__text-container__title" level={6}>
						Invite Your Members
					</Typography.Title>
					<Typography.Paragraph className="sb-invite-banner__text-container__subtitle" data-id="lblInviteMembersHelps">
						Help members quickly find your content by sharing this link or by searching for your unique name in the
						SWEATBASE app.
					</Typography.Paragraph>
				</div>
			</Flex>
			<Flex flex={1} className="sb-invite-banner__links-container">
				<div className="sb-invite-banner__links-container__link-group">
					<h1 className="sb-invite-banner__links-container__title">INVITE LINK</h1>
					<div className="sb-invite-banner__links-container__link-group__outline-bar">
						<span
							style={{
								marginBottom: "0px",
								color: "black",
								fontSize: "14px",
								fontWeight: 500,
								marginLeft: "8px",
								whiteSpace: "nowrap",
								textOverflow: "ellipsis",
								overflow: "hidden",
								width: "80%",
							}}
							data-id="lblInviteLink"
						>
							{inviteLink}
						</span>
						<div
							onClick={() => handleCopyText(inviteLink)}
							style={{ backgroundColor: primaryColor }}
							className="sb-invite-banner__links-container__link-group__outline-bar__copy-button"
							data-id="btnCopyLink"
						>
							<CopyIcon color="white" />
						</div>
					</div>
				</div>
				<div className="sb-invite-banner__links-container__link-group">
					<h1 className="sb-invite-banner__links-container__title">UNIQUE NAME</h1>
					<div className="sb-invite-banner__links-container__link-group__outline-bar">
						<span
							style={{
								marginBottom: "0px",
								color: "black",
								fontSize: "14px",
								fontWeight: 500,
								marginLeft: "8px",
								whiteSpace: "nowrap",
								textOverflow: "ellipsis",
								overflow: "hidden",
								width: "80%",
							}}
							data-id="lblUniqueName"
						>
							{domainGymResponse?.data?.name || ""}
						</span>
						<div
							className="sb-invite-banner__links-container__link-group__outline-bar__copy-button"
							style={{ backgroundColor: primaryColor }}
							onClick={() => handleCopyText(domainGymResponse?.data?.name)}
							data-id="btnCopyName"
						>
							<CopyIcon color="white" />
						</div>
					</div>
				</div>
			</Flex>
		</Flex>
	);
}

export default InviteBanner;

